import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ChildRows,
	type Props as ChildRowsProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows/index.tsx';
import { ChildRowsLoader } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows-loader/index.tsx';
import type {
	RenderChildRowsArgs,
	RenderCreateIssueController,
	RenderDraggableWrapper,
	RenderDraggableWrapperProps,
	RenderRows,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import type { renderChildRows_nativeIssueTable_RenderChildRowsComp$key } from '@atlassian/jira-relay/src/__generated__/renderChildRows_nativeIssueTable_RenderChildRowsComp.graphql';
import type { RenderTableCell } from '@atlassian/jira-issue-table-hierarchy/src/ui/loading-row/index.tsx';
import { Rows } from '../../../rows/index.tsx';
import {
	CHILD_ITEMS_EXPERIENCE,
	CHILD_ITEMS_LOAD_MORE_EXPERIENCE,
	ANALYTICS_SOURCE,
	MAX_COLUMNS,
	ACTION_COLUMNS,
	CHILD_QUERY_PAGE_SIZE,
} from '../../../../common/constants.tsx';
import {
	useIsReparentingEnabled,
	useSortDirection,
	useSortField,
	useJql,
	useIsFixedTableLayoutEnabled,
	useIsIssueRankDescending,
	useIsHideDoneItemsEnabled,
	useIsTimelineEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { TableCell } from '../../../../common/ui/table-cell/index.tsx';
import { useScrollStateSelector } from '../../../../controllers/scroll-state/index.tsx';
import { CreateIssueController } from '../../../../services/issue-create-mutation/index.tsx';
import { DraggableRowsContainer } from '../../../../controllers/draggable-rows/context.tsx';
import type { Column } from '../../../../common/types.tsx';

const renderDraggableWrapper: RenderDraggableWrapper = (props: RenderDraggableWrapperProps) => {
	return (
		<DraggableRowsContainer
			issueConnectionId={props.issueConnectionId}
			instanceId={props.instanceId}
		>
			{props.children}
		</DraggableRowsContainer>
	);
};
const renderRows: RenderRows = (rowProps) => <Rows {...rowProps} />;
const renderTableCell: RenderTableCell = (tableCellProps) => <TableCell {...tableCellProps} />;

export const RenderChildRowsComp = ({
	projectKey,
	isGroupingSupported,
	columns,
	rankField,
	...childRowsProps
}: RenderChildRowsArgs & {
	projectKey?: string;
	isGroupingSupported?: boolean;
	columns: Column[];
	rankField: renderChildRows_nativeIssueTable_RenderChildRowsComp$key | null;
}) => {
	const rankFieldData = useFragment<renderChildRows_nativeIssueTable_RenderChildRowsComp$key>(
		graphql`
			fragment renderChildRows_nativeIssueTable_RenderChildRowsComp on JiraJqlFieldWithAliases {
				fieldId
				jqlTerm
			}
		`,
		rankField,
	);
	/**
	 * Passing those values down to ChildRowsLoader as props to avoid direct imports
	 */
	const jql = useJql();
	const sortField = useSortField();
	const sortDirection = useSortDirection();
	const isReparentingEnabled = useIsReparentingEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const width = useScrollStateSelector((scrollState) => scrollState.width);
	const isIssueRankDescending = useIsIssueRankDescending();

	const renderCreateIssueController = useCallback<RenderCreateIssueController>(
		(props) => <CreateIssueController {...props} jql={jql} rankJqlTerm={rankFieldData?.jqlTerm} />,
		[jql, rankFieldData?.jqlTerm],
	);

	const renderChildRowsLoader = useCallback<ChildRowsProps['renderChildRowsLoader']>(
		(childRowsLoaderProps) => (
			<ChildRowsLoader
				{...childRowsLoaderProps}
				jql={jql}
				width={width}
				sortField={sortField}
				sortDirection={sortDirection}
				columns={columns}
				amountOfColumns={MAX_COLUMNS}
				actionColumns={ACTION_COLUMNS}
				pageSize={CHILD_QUERY_PAGE_SIZE}
				isReparentingEnabled={isReparentingEnabled}
				isTimelineEnabled={isTimelineEnabled}
				isIssueRankDescending={isIssueRankDescending}
				isHideDoneItemsEnabled={isHideDoneItemsEnabled}
				isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
				rankFieldId={rankFieldData?.fieldId}
				renderRows={renderRows}
				{...(fg('jsc_list_reranking_gate_technical') && {
					renderDraggableWrapper,
				})}
				renderTableCell={renderTableCell}
				renderCreateIssueController={renderCreateIssueController}
			/>
		),
		[
			columns,
			isFixedTableLayoutEnabled,
			isHideDoneItemsEnabled,
			isIssueRankDescending,
			isReparentingEnabled,
			isTimelineEnabled,
			jql,
			rankFieldData?.fieldId,
			renderCreateIssueController,
			sortDirection,
			sortField,
			width,
		],
	);

	return (
		<ChildRows
			{...childRowsProps}
			projectKey={projectKey}
			isGroupingSupported={isGroupingSupported}
			childItemsExperience={CHILD_ITEMS_EXPERIENCE}
			childItemsLoadMoreExperience={CHILD_ITEMS_LOAD_MORE_EXPERIENCE}
			analyticsSource={ANALYTICS_SOURCE}
			renderChildRowsLoader={renderChildRowsLoader}
		/>
	);
};
